// client/src/utils/contaPublicUrl.js
//
// Fonte única da URL pública de agendamento/página de uma Conta.
//
// NUNCA usar window.location.origin — o painel admin roda em
// admin.situai.com.br (porta 3000 em dev) e NAO serve /conta/*. Quem serve
// o site público da conta é o backend Express (porta 50000 em dev,
// PUBLIC_CONSUMER_DOMAIN = situai.site em produção).
//
// Regras de roteamento (ver server/loaders/routes.js):
//   - Conta SEM franquia:   <dominio>/conta/{slug}
//   - Conta COM franquia:   {slugFranquia}.<dominio>/{slug}
//
// Ignorar a franquia (ou usar o dominio errado) manda o usuario pra uma
// URL que nao resolve pra essa conta — bug confirmado ao vivo em
// 2026-09-07 (link "agenda.onmicare.com/..." e "situai.com.br/conta/...").

const SITE_CONTA_URL = process.env.REACT_APP_SITE_CONTA_URL || 'http://localhost:50000';

const DIACRITICS = /[̀-ͯ]/g;

const normalizarSlug = (valor) => {
    if (!valor) return '';
    return String(valor)
        .toLowerCase()
        .normalize('NFD')
        .replace(DIACRITICS, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const extrairSlugFranquia = (account) => {
    if (!account) return '';
    if (account.franquia && typeof account.franquia === 'object' && account.franquia.slug) {
        return account.franquia.slug;
    }
    return account.franquiaSlug || '';
};

const semProtocolo = (url) => url.replace(/^https?:\/\//, '');
const protocoloDe = (url) => (url.startsWith('http://') ? 'http://' : 'https://');

/**
 * Monta a URL publica completa da conta.
 * @param {object} account - documento (ou parcial) da conta.
 * @param {string} [slugOverride] - slug ainda nao salvo (formulario em edicao).
 * @returns {string|null} URL completa, ou null se nao ha slug.
 */
export const buildContaPublicUrl = (account, slugOverride) => {
    const slug = normalizarSlug(slugOverride || account?.slug);
    if (!slug) return null;

    const franquiaSlug = extrairSlugFranquia(account);
    if (franquiaSlug) {
        return `${protocoloDe(SITE_CONTA_URL)}${franquiaSlug}.${semProtocolo(SITE_CONTA_URL)}/${slug}`;
    }
    return `${SITE_CONTA_URL}/conta/${slug}`;
};

/**
 * Prefixo exibivel (sem o slug) — pra mostrar "https://situai.site/conta/"
 * ao lado de um campo editavel de slug.
 * @param {object} account
 * @returns {string}
 */
export const contaPublicUrlPrefix = (account) => {
    const franquiaSlug = extrairSlugFranquia(account);
    if (franquiaSlug) {
        return `${protocoloDe(SITE_CONTA_URL)}${franquiaSlug}.${semProtocolo(SITE_CONTA_URL)}/`;
    }
    return `${SITE_CONTA_URL}/conta/`;
};

// Site institucional (home, planos, políticas) — servido pelo mesmo backend
// que serve a API, SEMPRE no domínio do painel (PUBLIC_APP_DOMAIN =
// situai.com.br), nunca em situai.site. Deriva do REACT_APP_API_URL
// (https://api.situai.com.br/api/v1 -> https://situai.com.br).
const getInstitucionalBaseUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const m = apiUrl.match(/^(https?:\/\/)(?:api\.)?([^/]+)/i);
    if (m) return `${m[1]}${m[2]}`;
    return 'http://localhost:50000';
};

// `type` = Policy.type no backend (terms-of-use, privacy-policy). O SPA
// institucional renderiza a política publicada com esse type e audience
// 'platform_user' em /institucional/{type}.
export const getPoliticaPublicaUrl = (type) =>
    `${getInstitucionalBaseUrl()}/institucional/${type}`;

export default buildContaPublicUrl;
